import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAP_CENTER = [-4.0, 21.0];
const CIRCLE_OPTIONS = {
  color: '#4A6B48',
  fillColor: '#4A6B48',
  fillOpacity: 0.1
};
const MAP_STYLE = { height: '500px', borderRadius: '8px', border: '1px solid #E5E3DB' };

export default function MapPage() {
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProvinces = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/provinces`);
      setProvinces(response.data);
    } catch (error) {
      console.error('Error fetching provinces:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProvinces();
  }, [fetchProvinces]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="map-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="map-title"
            >
              Carte Interactive des Provinces Pilotes
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              Explorez nos 4 provinces pilotes : Sud-Kivu, Haut-Katanga, Kinshasa et Kongo Central.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          ) : (
            <>
              <div className="mb-8" data-testid="map-container">
                <MapContainer 
                  center={MAP_CENTER} 
                  zoom={6} 
                  style={MAP_STYLE}
                  data-testid="leaflet-map"
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {provinces.map((province) => {
                    const position = [province.coordinates.lat, province.coordinates.lng];
                    return (
                      <React.Fragment key={province.province_id}>
                        <Marker 
                          position={position}
                          data-testid={`map-marker-${province.name}`}
                        >
                          <Popup>
                            <div className="p-2">
                              <h3 className="font-bold text-[#4A6B48] mb-1">{province.name}</h3>
                              <p className="text-sm text-[#5C665A] mb-2">{province.description}</p>
                              <p className="text-xs text-[#D87D4A]">
                                {province.projects_count} projet{province.projects_count > 1 ? 's' : ''}
                              </p>
                            </div>
                          </Popup>
                        </Marker>
                        <Circle
                          center={position}
                          radius={50000}
                          pathOptions={CIRCLE_OPTIONS}
                        />
                      </React.Fragment>
                    );
                  })}
                </MapContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {provinces.map((province) => (
                  <div key={province.province_id} className="card" data-testid={`province-card-${province.name}`}>
                    <h3 
                      className="text-xl font-bold text-[#2E332D] mb-2"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {province.name}
                    </h3>
                    <p className="text-sm text-[#5C665A] leading-relaxed mb-3">
                      {province.description}
                    </p>
                    <div className="text-xs text-[#D87D4A] font-medium">
                      {province.projects_count} projet{province.projects_count > 1 ? 's' : ''} actif{province.projects_count > 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
